import uniqBy from 'lodash/uniqBy';
import BillingDocument from './BillingDocument.js';
import BillingListItem from './BillingListItem.js';
import BillingSummary from './BillingSummary.js';

/**
 * @property {object[]} _data - raw CustomerBill_v1 response data
 * @property {string} _invoiceBaseUrl - root url used to construct HTML and PDF invoice urls
 */
export default class BillingReport {
  /**
   * @param {object[]} data
   * @param {string} invoiceBaseUrl
   */
  constructor({ data, invoiceBaseUrl }) {
    this._data = data;
    this._invoiceBaseUrl = invoiceBaseUrl;
  }

  /**
   * @returns {BillingDocument[]}
   */
  get _billingDocuments() {
    return (
      this._data?.map(
        document => new BillingDocument({ ...document, invoiceBaseUrl: this._invoiceBaseUrl }),
      ) ?? []
    );
  }

  /**
   * @returns {BillingDocument[]}
   */
  get _unpaidBillingDocuments() {
    return this._billingDocuments.filter(document => document.isUnpaid || document.isPartiallyPaid);
  }

  /**
   * @returns {BillingDocument[]}
   */
  get _currentPeriodPaidDocuments() {
    return this.list[0].items.filter(document => document.isPaid);
  }

  /**
   * @returns {BillingPeriod}
   */
  get _latestBillingPeriod() {
    return this.list[0].period;
  }

  /**
   * @returns {?(BillingListItem[])} - list of items each representing one unique billing period with all of its related documents
   */
  get list() {
    if (this.isEmpty) {
      return null;
    }
    const periods = uniqBy(
      this._data.map(document => document.billingPeriod),
      'startDateTime',
    );
    return periods
      .map(
        period =>
          new BillingListItem({
            period,
            items: this._billingDocuments.filter(
              document => document.billingPeriod.startDateTime === period.startDateTime,
            ),
            invoiceBaseUrl: this._invoiceBaseUrl,
          }),
      )
      .sort((a, b) => b.period.from - a.period.from);
  }

  /**
   * @returns {?BillingSummary}
   */
  get summary() {
    if (this.isEmpty) {
      return null;
    }
    return new BillingSummary({
      currentPeriodPaidDocuments: this._currentPeriodPaidDocuments,
      unpaidBillingDocuments: this._unpaidBillingDocuments,
      latestBillingPeriod: this._latestBillingPeriod,
    });
  }

  /**
   * @returns {boolean}
   */
  get isEmpty() {
    return !this._data;
  }
}
